import React, { useEffect, useRef } from "react";
import { X, Compass, Network, HeartHandshake, Footprints } from "lucide-react";

// Chromatic palette borrowed from Stage 5 — Solid Reset (Chrome Water):
//   bg     : icy mint background       #EAF4F4
//   text   : dark teal headlines+body  #1A3636
//   accent : Chrome Water mint         #98D8D8
//   surface: clean white               #FFFFFF
const C = {
  bg: "#EAF4F4",
  text: "#1A3636",
  accent: "#98D8D8",
  surface: "#FFFFFF",
};

const SECTIONS = [
  {
    num: "01",
    icon: Compass,
    title: "Adaptation, Not Extinction",
    body: "The mammoth never truly vanished. It evolved. With 99.6% shared DNA, the elephant is living proof that survival isn't about staying the same — it's about adapting to the shift. In a world that feels increasingly unstable, we don't need to restart. We need to upgrade.",
  },
  {
    num: "02",
    icon: Network,
    title: "True Networks Over Rigid Systems",
    body: "Nature survives through energy-efficient, deeply connected ecosystems, not predatory competition. Elephant herds prioritize collective care, protecting every member through every stage of life. Somewhere along the way, human systems forgot this. We traded deep, intimate networks for massive, isolating frameworks.",
  },
  {
    num: "03",
    icon: HeartHandshake,
    title: "Humane over Human",
    body: "The collective anxiety of our generation — the quiet fear of extinction or self-destruction — comes from a system that treats us as a crowd rather than individuals. Our mission is to shift from human systems (rigid, top-down structures) to humane systems (intimate, individual care).",
  },
  {
    num: "04",
    icon: Footprints,
    title: "The Journey",
    body: "The mammoth is our blueprint. It represents a massive, gentle presence that moved through a changing world with intention, protecting its community and honoring its shared environment.",
  },
];

/**
 * Full-screen "Why a mammoth?" modal — Stage 5 (Solid Reset) chromatic skin.
 *  - Icy mint #EAF4F4 background
 *  - Dark teal #1A3636 typography
 *  - Mint #98D8D8 accent for pills, bars, icon discs, quote frame
 *  - Looping mammoth_loop.mp4 at the top (autoplay, muted, loop, no controls)
 *  - ESC, overlay click, top-right close pill, and footer button all dismiss
 */
export const WhyMammothModal = ({ onClose }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="why-mammoth-title"
      data-testid="why-mammoth-modal"
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ backgroundColor: C.bg, color: C.text, animation: "fadeUp 0.4s ease-out both" }}
      onClick={onClose}
    >
      {/* Top-right close pill — always visible while scrolling */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="why-mammoth-close"
        aria-label="Close and return to landing"
        className="fixed z-10 flex items-center gap-2 rounded-full font-bold uppercase tracking-widest transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        style={{
          right: "clamp(16px, 3vw, 32px)",
          top: "clamp(16px, 3vw, 32px)",
          backgroundColor: C.accent,
          color: C.text,
          paddingInline: "clamp(14px, 1.6vw, 22px)",
          height: "clamp(40px, 4.4vw, 52px)",
          fontSize: "clamp(10px, 1vw, 12px)",
          letterSpacing: "0.18em",
          boxShadow: "0 6px 18px rgba(26, 54, 54, 0.18)",
        }}
      >
        <X size={16} strokeWidth={3} />
        <span className="hidden sm:inline">Close</span>
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 pb-20"
      >
        {/* HEADER VIDEO */}
        <div className="pt-10 sm:pt-14">
          <div
            className="relative w-full overflow-hidden rounded-3xl"
            style={{
              aspectRatio: "16 / 9",
              boxShadow: "0 30px 60px rgba(26, 54, 54, 0.18)",
              border: `1px solid ${C.accent}55`,
            }}
          >
            <video
              ref={videoRef}
              src="/mammoth_loop.mp4"
              autoPlay
              loop
              muted
              playsInline
              data-testid="mammoth-video"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                backgroundColor: C.bg,
              }}
            />
          </div>
        </div>

        {/* TITLE */}
        <div className="mt-10 sm:mt-14">
          <div
            className="font-bold uppercase"
            style={{
              color: C.text,
              opacity: 0.6,
              fontSize: "clamp(11px, 1.1vw, 13px)",
              letterSpacing: "0.32em",
            }}
          >
            Why a mammoth ?
          </div>
          <h2
            id="why-mammoth-title"
            className="font-display font-black tracking-tighter mt-3 leading-[0.92]"
            style={{ color: C.text, fontSize: "clamp(2.4rem, 8vw, 5.5rem)" }}
          >
            The Mammoth Shift
          </h2>
          <div
            className="mt-6 h-[3px] rounded-full"
            style={{ backgroundColor: C.accent, width: "clamp(80px, 12vw, 140px)" }}
          />
        </div>

        {/* SECTIONS */}
        <div className="mt-12 sm:mt-16 space-y-12 sm:space-y-14">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.num}
                className="grid sm:grid-cols-[auto_1fr] gap-5 sm:gap-8"
                data-testid={`why-section-${s.num}`}
              >
                <div className="flex sm:flex-col items-center sm:items-center gap-3 sm:gap-4">
                  <div
                    className="font-display font-black leading-none"
                    style={{
                      color: C.text,
                      opacity: 0.55,
                      fontSize: "clamp(1.5rem, 3vw, 2.2rem)",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {s.num}
                  </div>
                  <div
                    className="rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: `${C.accent}55`,
                      border: `1.5px solid ${C.accent}`,
                      width: "clamp(48px, 5vw, 64px)",
                      height: "clamp(48px, 5vw, 64px)",
                      color: C.text,
                    }}
                  >
                    <Icon size={22} strokeWidth={2} />
                  </div>
                </div>

                <div>
                  <h3
                    className="font-display font-bold tracking-tight"
                    style={{
                      color: C.text,
                      fontSize: "clamp(1.35rem, 2.4vw, 1.85rem)",
                      lineHeight: 1.15,
                    }}
                  >
                    {s.title}
                  </h3>
                  <p
                    className="mt-3"
                    style={{
                      color: C.text,
                      opacity: 0.82,
                      fontSize: "clamp(0.98rem, 1.4vw, 1.1rem)",
                      lineHeight: 1.55,
                    }}
                  >
                    {s.body}
                  </p>
                </div>
              </article>
            );
          })}
        </div>

        {/* CLOSING MANIFESTO */}
        <div
          className="mt-14 sm:mt-20 p-6 sm:p-10 rounded-3xl"
          style={{
            backgroundColor: `${C.accent}55`,
            border: `1.5px solid ${C.accent}`,
          }}
        >
          <p
            className="font-display italic"
            style={{
              color: C.text,
              fontSize: "clamp(1.15rem, 2.2vw, 1.6rem)",
              lineHeight: 1.4,
            }}
          >
            We choose the mammoth because real evolution doesn't happen all at once.
            <br />
            It's a journey through a shifting landscape. One step at a time.
          </p>
        </div>

        {/* FOOTER EXIT BUTTON */}
        <div className="mt-12 sm:mt-16 flex flex-col items-center gap-3">
          <button
            onClick={onClose}
            data-testid="why-mammoth-footer-back"
            className="rounded-full font-bold uppercase tracking-widest transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{
              backgroundColor: C.accent,
              color: C.text,
              paddingInline: "clamp(24px, 3vw, 36px)",
              height: "clamp(48px, 5vw, 56px)",
              fontSize: "clamp(11px, 1.1vw, 13px)",
              letterSpacing: "0.2em",
              boxShadow: "0 10px 28px rgba(152, 216, 216, 0.45)",
            }}
          >
            ← Back to landing
          </button>
          <div className="text-xs opacity-50 tracking-wider">
            Press <kbd className="px-1.5 py-0.5 rounded border" style={{ borderColor: `${C.text}40` }}>esc</kbd> or click anywhere outside to close
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhyMammothModal;
